import { useCallback } from 'react';
import moment from 'moment';
import { GeoJSONSourceRaw, Layer, Map as MapboxMap, Popup } from 'mapbox-gl';
import { WayPoint } from '@bringg/types';

const useMapboxWayPoints = (map: MapboxMap, wayPoints: WayPoint[], timeFormat: string, timeZone?: string) => {
	const source: GeoJSONSourceRaw = {
		type: 'geojson',
		data: {
			type: 'Feature',
			properties: {
				description: ''
			},
			geometry: {
				type: 'Point',
				coordinates: []
			}
		}
	};

	const drawWayPoints = useCallback(() => {
		wayPoints.forEach((wayPoint, index) => {
			const id = `point${index}`;

			const dataSource = source.data as GeoJSON.Feature<GeoJSON.Point>;

			const etaTime = wayPoint.eta
				? timeZone
					? moment(wayPoint.eta).tz(timeZone).format(timeFormat)
					: moment(wayPoint.eta).format(timeFormat)
				: '';

			dataSource.properties.description = `
                        <div>
                        <span class="title">ETA </span>
                        <span class="eta">${etaTime}</span>
                        </div>`;
			dataSource.geometry.coordinates = [wayPoint.lng, wayPoint.lat];

			if (map.getLayer(id)) {
				map.removeLayer(id);
			}

			if (map.getSource(id)) {
				map.removeSource(id);
			}
			map.addSource(id, source);

			const layer = generateLayer(id, wayPoint);
			map.addLayer(layer);

			// Create a popup, but don't add it to the map yet.
			const popup = new Popup({
				closeButton: false,
				closeOnClick: false,
				className: 'mapbox-route-tracker-popup',
				offset: [0, -30]
			});

			map.on('mouseenter', id, event => {
				map.getCanvas().style.cursor = 'pointer';

				const feature = event.features[0];
				const coordinates = (feature.geometry as GeoJSON.Point).coordinates.slice();
				const description = feature.properties.description;

				// Ensure that if the map is zoomed out such that multiple
				// copies of the feature are visible, the popup appears
				// over the copy being pointed to.
				while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
					coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
				}

				popup
					.setLngLat(coordinates as [number, number])
					.setHTML(description)
					.addTo(map);
			});

			map.on('mouseleave', id, () => {
				map.getCanvas().style.cursor = '';
				popup.remove();
			});
		});
	}, [map, wayPoints]);

	const generateLayer = (id: string, wayPoint: WayPoint): Layer => {
		return {
			id: id,
			source: id,
			type: 'symbol',
			layout: {
				'icon-image': `marker-${wayPoint.late ? 'red' : 'blue'}`,
				'icon-rotation-alignment': 'map',
				'icon-allow-overlap': true,
				'icon-ignore-placement': true,
				'icon-keep-upright': true,
				'icon-size': 1,
				'icon-anchor': 'bottom',
				'text-field': wayPoint.position?.toString() || '',
				'text-anchor': 'bottom',
				'text-offset': [0, -0.35],
				'text-font': ['literal', ['Rubik Medium']],
				'text-size': 13
			},
			paint: {
				'text-color': '#ffffff'
			}
		};
	};

	return { drawWayPoints };
};

export default useMapboxWayPoints;
